import cx from 'classnames';
import {HTMLReactParserOptions, Element as ParserElement, domToReact, DOMNode} from 'html-react-parser';
import {filter, includes, isEmpty, map} from 'lodash';
import moment from 'moment';
import {Fragment, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Icon, ClientDynamicContent, DynamicContent, FileInput, NodeElement,
    LoadingDots, Loader, SessionTextChatViewProps, FormattedMessage
} from 'components';
import {DynamicComponentNames, TextChatUnits as Units, TEXT_CHAT_DEFAULT_TEMPLATE} from 'constants/client';
import {SessionTextChatMessageOwner, VoiceInputMode} from 'enums';
import {useClientSession} from 'hooks';
import {AnyParticipant, Session} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {
    DefaultParticipant, ParticipantRole, PreSessionFlowCompletion, PreSessionFlowState, TextChatContentType, TextChatMessage
} from 'types';
import {dynamicContentUtils, sessionUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT;

type MessageTemplateProps = {
    message: TextChatMessage;
    renderIcon: (message: TextChatMessage) => React.ReactNode;
    renderContent: (message: TextChatMessage) => React.ReactNode;
    renderParticipant: (message: TextChatMessage) => React.ReactNode;
    renderMessageOptions: (message: TextChatMessage) => React.ReactNode;
}

const {isMatchElement, isMatchElementInTree, isMatchLanguage, nodeElementParent, renderDefaultNode} = dynamicContentUtils;

const buildMessageTemplateOptionsGetter = (templateUnit: string) => (props: MessageTemplateProps): HTMLReactParserOptions => {
    const {message, renderContent, renderParticipant, renderIcon, renderMessageOptions} = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(templateUnit, node)) {
                return nodeElementParent({show: true});
            }
            if (isMatchElement(Units.MessageIcon, node)) {
                return <NodeElement show={!!message.content} node={node}>{renderIcon(message)}</NodeElement>;
            }
            if (isMatchElement(Units.MessageParticipant, node)) {
                return <NodeElement show={!!message.content} node={node}>{renderParticipant(message)}</NodeElement>;
            }
            if (isMatchElement(Units.MessageContent, node)) {
                return <NodeElement show={!!message.content} node={node}>{renderContent(message)}</NodeElement>;
            }
            if (isMatchElement(Units.MessageTime, node)) {
                const format = node.attribs?.format || 'HH:mm';
                return <NodeElement show={!!message.content} node={node}>{moment(message.date).format(format)}</NodeElement>;

            }
            if (!isEmpty(message?.data?.options) && isMatchElement(Units.InteractiveOptions, node)) {
                return <NodeElement node={node}>{renderMessageOptions(message)}</NodeElement>;
            }

            if (!isMatchLanguage(node)) {
                // eslint-disable-next-line react/jsx-no-useless-fragment
                return <Fragment />;
            }

            return nodeElementParent({show: isMatchElementInTree(templateUnit, node)});
        }
    };
};

const getSystemMessageTemplateOptions = buildMessageTemplateOptionsGetter(Units.SystemMessageTemplate);
const getChatbotMessageTemplateOptions = buildMessageTemplateOptionsGetter(Units.ChatbotMessageTemplate);
const getOwnMessageTemplateOptions = buildMessageTemplateOptionsGetter(Units.OwnMessageTemplate);
const getForeignMessageTemplateOptions = buildMessageTemplateOptionsGetter(Units.ForeignMessageTemplate);

const getOptions = (props: any, template: string): HTMLReactParserOptions => {
    const {
        t, messageInputText, onChangeMessage, messages, localParticipantId, renderContent, renderParticipant,
        openFileFileInput, onSubmit, submitDisabled, formDisabled, typingRemoteParticipantIds, renderForm, dockedPanel,
        renderTypingIndicator, renderIcon, renderNoMessages, messagesEndRef, renderMessageOptions, localParticipant,
        voiceInput, voiceInputHandlers, renderVoiceInputButton, renderVoiceListening
    } = props;

    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.SystemMessageTemplate, node) ||
                isMatchElement(Units.ForeignMessageTemplate, node) ||
                isMatchElement(Units.ChatbotMessageTemplate, node) ||
                isMatchElement(Units.OwnMessageTemplate, node)) {
                return nodeElementParent({show: false});
            }

            const isEmptyChat = isEmpty(messages);

            if (isMatchElement(Units.Messages, node)) {
                return (
                    <NodeElement node={node} show={!isEmptyChat}>
                        {map(messages, (message: TextChatMessage, index: number) => {
                            const messageOwner = sessionUtils.getTextChatMessageOwner(message.from.id, localParticipantId);
                            const isLast = index === messages.length - 1;

                            let optionsGetter = getOwnMessageTemplateOptions;
                            if (messageOwner === SessionTextChatMessageOwner.System) {
                                optionsGetter = getSystemMessageTemplateOptions;
                            } else if (messageOwner === SessionTextChatMessageOwner.Chatbot) {
                                optionsGetter = getChatbotMessageTemplateOptions;
                            } else if (messageOwner === SessionTextChatMessageOwner.Foreign) {
                                optionsGetter = getForeignMessageTemplateOptions;
                            }

                            const renderOptions = isLast ? renderMessageOptions : () => null;

                            return (
                                <DynamicContent
                                    key={message.id}
                                    template={template}
                                    optionsProps={{
                                        message, renderContent, renderParticipant, renderIcon, renderMessageOptions: renderOptions
                                    }}
                                    optionsGetter={optionsGetter}
                                />
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Form, node)) {
                const options = getOptions(props, template);
                return (
                    <NodeElement
                        node={node}
                        disabled={formDisabled}
                        onSubmit={onSubmit}
                    >
                        {domToReact(node.children as DOMNode[], options)}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.FormDefault, node)) {
                return (
                    <NodeElement node={node}>
                        {dockedPanel}
                        {renderForm()}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.NoMessages, node)) {
                return (
                    <NodeElement node={node} show={isEmptyChat && isEmpty(typingRemoteParticipantIds)}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.NoMessagesDefault, node)) {
                return (
                    <NodeElement node={node} show={isEmptyChat && isEmpty(typingRemoteParticipantIds)}>
                        {renderNoMessages()}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.AttachFileButton, node)) {
                return (
                    <NodeElement node={node} disabled={formDisabled} onClick={openFileFileInput}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.SendMessageButton, node)) {
                return (
                    <NodeElement node={node} disabled={submitDisabled} onClick={onSubmit}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.VoiceInputButton, node)) {
                return (
                    <NodeElement
                        node={node}
                        show={!!voiceInput?.enabled}
                        disabled={formDisabled}
                        {...voiceInputHandlers}
                    >
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.VoiceInputButtonDefault, node)) {
                return (
                    <NodeElement node={node} show={!!voiceInput?.enabled}>
                        {renderVoiceInputButton()}
                    </NodeElement>);
            }
            if (isMatchElement(Units.VoiceListening, node)) {
                return (
                    <NodeElement node={node} show={!!voiceInput?.recording}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.VoiceListeningDefault, node)) {
                return (
                    <NodeElement node={node} show={!!voiceInput?.recording}>
                        {renderVoiceListening()}
                    </NodeElement>);
            }
            if (isMatchElement(Units.TypingIndicator, node)) {
                return (
                    <NodeElement node={node} show={!isEmpty(typingRemoteParticipantIds)}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>);
            }
            if (isMatchElement(Units.TypingIndicatorDefault, node)) {
                return (
                    <NodeElement node={node} show={!isEmpty(typingRemoteParticipantIds)}>
                        {renderTypingIndicator(typingRemoteParticipantIds)}
                    </NodeElement>);
            }
            if (isMatchElement(Units.MessageInputContainer, node)) {
                return (
                    <NodeElement node={node}>
                        <input
                            type='text'
                            value={messageInputText}
                            placeholder={node.attribs.placeholder || t(T_KEYS.INPUT_PLACEHOLDER)}
                            maxLength={1000}
                            onChange={(event) => onChangeMessage(event.target.value)}
                        />
                    </NodeElement>
                );
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const ClientSessionTextChatView: React.FC<SessionTextChatViewProps> = (props) => {
    const {
        message, messages, fileIsUploading, localParticipantId,
        downloadingFiles, typingRemoteParticipantIds, downloadFileDisabled, participants,
        submitDisabled, messagesEndRef, setSendFileInputRef, onChange, downloadFile, onSubmit, handleUploadFile, openFileFileInput,
        onOptionSelect, formDisabled, optionSelectInProgress, getTranslation, uploadFileHidden, voiceInput, dockedPanel
    } = props;

    const {t} = useTranslation();
    const {textChatActivity, avatarActivity} = useClientSession();
    const activityData = (textChatActivity || avatarActivity)?.data as {preSessionFlow?: PreSessionFlowState} | undefined;
    const flowState = activityData?.preSessionFlow;
    const awaitingConfirmation = !!flowState?.flowSnapshot.modes?.chatbot && !!flowState?.awaitingConfirmation;
    const endsSession = [PreSessionFlowCompletion.End, PreSessionFlowCompletion.Chat].includes(flowState?.flowSnapshot.onComplete);
    const completionMessage = useMemo<Partial<TextChatMessage> | null>(() => {
        if (!awaitingConfirmation) {
            return null;
        }
        const keys = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;
        // This local status must be visible even when the provider cannot produce a closing reply.
        return {
            id: 'pre-session-completion',
            date: new Date().toISOString(),
            from: {id: DefaultParticipant.Chatbot, name: null},
            contentType: TextChatContentType.Chatbot,
            content: t(endsSession ? keys.COMPLETED_DESCRIPTION : keys.READY_DESCRIPTION),
        };
    }, [awaitingConfirmation, endsSession, t]);

    // The thread only scrolls itself on new messages. A docked panel opening or growing (a camera preview, a
    // vendor frame) shrinks the thread from below and would bury the message that asked for the step.
    useEffect(() => {
        const container = messagesEndRef?.current?.parentElement;
        if (!dockedPanel || !container || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => messagesEndRef.current?.scrollIntoView({block: 'end'}));
        observer.observe(container.parentElement || container);
        return () => observer.disconnect();
    }, [messagesEndRef, dockedPanel]);

    const handleVoiceStart = useCallback(() => voiceInput?.onStart(), [voiceInput]);
    const handleVoiceStop = useCallback(() => voiceInput?.onStop(), [voiceInput]);
    const handleVoiceToggle = useCallback(
        () => (voiceInput?.recording ? voiceInput?.onStop() : voiceInput?.onStart()), [voiceInput]
    );

    // One source of truth for both the default button and the white-label unit, so a custom template behaves
    // exactly like the built-in one. Toggle deliberately has no mouseleave/touchcancel: those stop a HELD turn,
    // and here they would switch the microphone off as soon as the pointer left the button.
    const voiceInputHandlers = useMemo(() => (voiceInput?.mode === VoiceInputMode.Toggle
        ? {onClick: handleVoiceToggle, 'aria-pressed': !!voiceInput?.recording}
        : {
            // Hold to talk: release (or drag the cursor away) always stops the recording,
            // so a lost mouseup can't leave the microphone open.
            onMouseDown: handleVoiceStart,
            onMouseUp: handleVoiceStop,
            onMouseLeave: handleVoiceStop,
            onTouchStart: handleVoiceStart,
            onTouchEnd: handleVoiceStop,
            onTouchCancel: handleVoiceStop
        }), [voiceInput?.mode, voiceInput?.recording, handleVoiceToggle, handleVoiceStart, handleVoiceStop]);

    const visibleMessages = useMemo(() => filter(messages, (m) =>
        /* !!m.content && */ m.contentType !== TextChatContentType.IdleReminder), [messages]);
    const conversationMessages = useMemo(() => completionMessage ? [...visibleMessages, completionMessage] : visibleMessages,
        [visibleMessages, completionMessage]);

    const renderIcon = useCallback((msg: Partial<TextChatMessage>) => {
        const senderRole = sessionUtils.getParticipantById({participants} as any as Session<AnyParticipant>, msg.from?.id)?.role;
        if (msg?.from?.id === DefaultParticipant.Chatbot) {
            return <Icon name='chatbot' size='lg' color='alternative' />;
        } else if (msg?.from?.id === DefaultParticipant.System) {
            return <Icon name='camera' size='xl' />;
        } else if (senderRole === ParticipantRole.Agent) {
            return <Icon name='agent-participant' color='dark' size='lg' />;
        } else if (senderRole === ParticipantRole.Assistant) {
            return <Icon name='assistant-participant' color='dark' size='md' />;
        } else if (senderRole === ParticipantRole.Guest) {
            return <Icon name='guest-participant' color='dark' size='md' />;
        } else if (senderRole === ParticipantRole.Client) {
            return <Icon name='client-participant' color='dark' size='md' />;
        }

        return <Icon name='agent-participant' color='dark' size='lg' />;
    }, [participants]);

    const renderInteractiveChatMsg = useCallback((msg: TextChatMessage) => {
        const isSuggestion = T_KEYS.INTERACTIVE_CHAT_CODE[buildTranslationKey(msg.content)];
        const defaultContent = isSuggestion ? t(T_KEYS.INTERACTIVE_CHAT_CODE[buildTranslationKey(msg.content)]) : msg.content;
        return <FormattedMessage message={defaultContent} />;
    }, [t]);

    const renderContent = useCallback((msg: TextChatMessage) => {
        const isDownloading = includes(downloadingFiles, msg.id);
        return (
            <div className={cx('message-content', {
                'interactive-chat': msg.contentType === TextChatContentType.InteractiveChat,
                'interactive-chat-answer': [
                    TextChatContentType.InteractiveChatAnswer,
                    TextChatContentType.ChatbotConfirmation
                ].includes(msg.contentType),
                'interactive-chat-error': msg.contentType === TextChatContentType.InteractiveChatError
            })}
            >
                {msg.contentType === TextChatContentType.File &&
                    <div
                        className={cx('file', {loading: isDownloading, disabled: downloadFileDisabled})}
                        onClick={() => !downloadFileDisabled && !isDownloading && downloadFile(msg)}
                    >
                        {isDownloading && <Loader />}
                        <Icon name='file-text' className='file-icon' color='primary' />
                        <span className='file-name'>{msg.content}</span>
                    </div>}
                {msg.contentType === TextChatContentType.Text &&
                    <div className='text'>
                        <FormattedMessage message={getTranslation(msg)} />
                    </div>}

                {msg.contentType === TextChatContentType.InteractiveChat &&
                    <div className='text'>{renderInteractiveChatMsg(msg)}</div>}
                {msg.contentType === TextChatContentType.InteractiveChatAnswer &&
                    <div className='text'>
                        <FormattedMessage message={msg.content} />
                    </div>}
                {msg.contentType === TextChatContentType.Chatbot &&
                    <div className='text'>
                        <FormattedMessage message={msg.content} />
                    </div>}
                {msg.contentType === TextChatContentType.ChatbotConfirmation &&
                    <div className='text'>
                        <FormattedMessage message={msg.content} />
                    </div>}
                {msg.contentType === TextChatContentType.InteractiveChatError &&
                    <div className='text'>
                        <FormattedMessage message={t(T_KEYS.INTERACTIVE_CHAT_ERROR[buildTranslationKey(msg.content)])} />
                    </div>}
            </div>
        );
    }, [downloadingFiles, downloadFileDisabled, renderInteractiveChatMsg, t, downloadFile, getTranslation]);

    const renderParticipant = useCallback((msg: TextChatMessage) => (
        <div className='message-participant'>
            {sessionUtils.getParticipantNameById({participants} as Session<AnyParticipant>, msg.from.id)}
        </div>
    ), [participants]);

    const renderMessageOptions = useCallback((msg: TextChatMessage) => {
        if (isEmpty(msg.data?.options)) {
            return null;
        }

        return (
            <div className='interactive-options'>
                {map(msg.data.options, (option: {content: string}, optionIndex: number) => (
                    <div
                        key={optionIndex}
                        className='interactive-option'
                        onClick={optionSelectInProgress ? null : () => onOptionSelect?.(optionIndex)}
                    >
                        {option.content}
                    </div>
                ))}
            </div>
        );
    }, [optionSelectInProgress, onOptionSelect]);

    const handleOpenFileFileInput = useCallback(() => {
        if (fileIsUploading || formDisabled) {
            return;
        }
        openFileFileInput();
    }, [fileIsUploading, formDisabled, openFileFileInput]);

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        if (formDisabled || submitDisabled) {
            return;
        }
        onSubmit?.(event);
    }, [formDisabled, submitDisabled, onSubmit]);

    const renderVoiceInputButton = useCallback((className?: string) => voiceInput?.enabled &&
        <Icon
            name='microphone-on'
            color={voiceInput.recording ? 'success' : 'primary'}
            button
            disabled={formDisabled}
            className={className}
            {...voiceInputHandlers}
        />, [voiceInput, formDisabled, voiceInputHandlers]);

    const renderVoiceListening = useCallback(() => (
        <Fragment>
            {t(T_KEYS.LISTENING)}
            <LoadingDots dotColor='success' size='small' />
        </Fragment>
    ), [t]);

    const renderForm = useCallback(() => onSubmit &&
        <form
            className={cx('form', {disabled: formDisabled})}
            onSubmit={handleSubmit}
        >
            <div className='form-input-wrapper'>
                <input
                    className='form-input'
                    type='text'
                    value={message}
                    readOnly={formDisabled}
                    placeholder={t(T_KEYS.INPUT_PLACEHOLDER)}
                    maxLength={1000}
                    onChange={(event) => onChange(event.target.value)}
                />
                <Icon
                    name='send'
                    color='primary'
                    button
                    className='submit-btn'
                    margin={false}
                    disabled={submitDisabled}
                    onClick={handleSubmit}
                />
                {voiceInput?.recording &&
                    <div className='voice-listening'>
                        {renderVoiceListening()}
                    </div>}
            </div>

            {renderVoiceInputButton('attach-btn')}

            {fileIsUploading && !uploadFileHidden &&
                <div className='submit-btn-loading'><Loader /></div>}

            {!fileIsUploading && !uploadFileHidden &&
                <Icon
                    name='paper-clip'
                    color='primary'
                    button
                    disabled={formDisabled}
                    className='attach-btn'
                    onClick={handleOpenFileFileInput}
                />}
        </form>,
    [t, onSubmit, handleSubmit, onChange, handleOpenFileFileInput, voiceInput, renderVoiceInputButton, renderVoiceListening,
        formDisabled, fileIsUploading, message, submitDisabled, uploadFileHidden]);

    const renderTypingIndicator = useCallback((participantIds: string[]) => (
        map(participantIds, (id) => {
            const messageOwner = sessionUtils.getTextChatMessageOwner(id, localParticipantId);
            return (
                <div key={id} className='typing-indicator'>
                    <div className={cx('message', {
                        foreign: messageOwner === SessionTextChatMessageOwner.Foreign,
                        chatbot: messageOwner === SessionTextChatMessageOwner.Chatbot,
                        system: messageOwner === SessionTextChatMessageOwner.System
                    })}
                    >
                        <div className='message-icon'>{renderIcon({from: {id, name: null}})}</div>
                        <div className='message-content'>
                            <LoadingDots dotColor='primary' size='small' />
                        </div>
                    </div>
                </div>
            );
        })
    ), [localParticipantId, renderIcon]);

    const renderNoMessages = useCallback(() => t(T_KEYS.NO_MESSAGES), [t]);

    return (
        <Fragment>
            <ClientDynamicContent
                component={DynamicComponentNames.TextChat}
                defaultTemplate={TEXT_CHAT_DEFAULT_TEMPLATE}
                optionsProps={{
                    t,
                    messageInputText: message,
                    localParticipantId,
                    messages: conversationMessages,
                    submitDisabled,
                    formDisabled,
                    typingRemoteParticipantIds,
                    messagesEndRef,
                    onChangeMessage: onChange,
                    renderIcon,
                    renderParticipant,
                    renderContent,
                    renderForm,
                    dockedPanel,
                    renderMessageOptions,
                    renderTypingIndicator,
                    renderNoMessages,
                    openFileFileInput: handleOpenFileFileInput,
                    onSubmit: handleSubmit,
                    voiceInput,
                    voiceInputHandlers,
                    renderVoiceInputButton,
                    renderVoiceListening,
                }}
                optionsGetter={getOptions}
            />
            <FileInput
                setInputRef={setSendFileInputRef}
                onUpload={handleUploadFile}
            />
        </Fragment>
    );
};
